import { normalizeIdPart } from './loadingStateKeys';
import { resolveLessonMaterials } from './instructionStepUtils';
import { splitSupportingMaterialContent } from './supportingMaterialContentSegments';
import type {
    InstructionStepDto,
    LessonDetailDto,
    RasterOptionDto,
    StepPlannedFigureDto,
    StepRasterFigureDto,
    SupportingMaterialDto,
    SupportingMaterialVideoOptionDto,
    UnitDto,
} from './types';

export type MediaReviewSlotKind = 'video' | 'raster';

export type MediaReviewSlotRef = {
    kind: MediaReviewSlotKind;
    lessonId: string;
    materialId: string;
    figureKey?: string;
};

export type MediaReviewBlockReason =
    | 'video-unapproved'
    | 'image-unapproved'
    | 'image-generating'
    | 'image-awaiting'
    | 'image-failed';

export type PendingMediaApprovalReason = 'pending-video' | 'pending-image' | 'pending-media';

export type FailedVectorDiagramRef = {
    lessonId: string;
    stepId: string;
    figureKey: string;
    materialKind: 'reading' | 'worksheet';
};

export type UnitApprovalBlockedReason = PendingMediaApprovalReason | MediaReviewBlockReason | 'diagram-failed';

export type MediaReviewBlock = MediaReviewSlotRef & {
    reason: MediaReviewBlockReason;
};

/** Strip virtual material suffix (`{stepId}:30`) for C322/C323/C336 step ids. */
export function resolveStepIdForMediaCommand(materialId: string | undefined): string | undefined {
    if (!materialId) return undefined;
    const colonIdx = materialId.indexOf(':');
    if (colonIdx > 0) return materialId.slice(0, colonIdx);
    return materialId;
}

export function videoSelectionStateKey(
    unitId: unknown,
    lessonId: unknown,
    materialId: unknown
): string {
    return `${normalizeIdPart(unitId)}|${normalizeIdPart(lessonId)}|${normalizeIdPart(materialId)}`;
}

export function rasterSelectionStateKey(
    unitId: unknown,
    lessonId: unknown,
    stepId: unknown,
    figureKey: unknown
): string {
    return `${normalizeIdPart(unitId)}|${normalizeIdPart(lessonId)}|${normalizeIdPart(stepId)}|${normalizeIdPart(figureKey)}`;
}

export function rasterApprovalStateKey(
    unitId: unknown,
    lessonId: unknown,
    stepId: unknown,
    figureKey: unknown
): string {
    return rasterSelectionStateKey(unitId, lessonId, stepId, figureKey);
}

export function vectorDiagramDomId(
    unitId: string,
    lessonId: string,
    stepId: string,
    figureKey: string
): string {
    return `vector-diagram-${normalizeIdPart(unitId)}-${normalizeIdPart(lessonId)}-${normalizeIdPart(stepId)}-${normalizeIdPart(figureKey)}`;
}

export function findFailedDiagramKeyInContent(content: string | undefined | null): string | null {
    if (!content?.trim()) return null;
    for (const segment of splitSupportingMaterialContent(content)) {
        if (segment.type === 'figure' && segment.failed) {
            return segment.key;
        }
    }
    return null;
}

export function findFirstFailedVectorDiagram(unit: UnitDto): FailedVectorDiagramRef | null {
    for (const lesson of unit.e251_Lessons ?? []) {
        const lessonId = lesson.e251_LessonId ? String(lesson.e251_LessonId) : '';
        if (!lessonId) continue;
        for (const step of lesson.e251_InstructionSteps ?? []) {
            const stepId = step.e251_StepId ? String(step.e251_StepId) : '';
            if (!stepId) continue;

            const readingKey = findFailedDiagramKeyInContent(step.e251_Reading?.e251_ReadingContent);
            if (readingKey) {
                return { lessonId, stepId, figureKey: readingKey, materialKind: 'reading' };
            }

            const worksheetKey = findFailedDiagramKeyInContent(
                step.e251_WorksheetSection?.e251_WorksheetContent
            );
            if (worksheetKey) {
                return { lessonId, stepId, figureKey: worksheetKey, materialKind: 'worksheet' };
            }
        }
    }
    return null;
}

export function mediaSlotDomId(
    unitId: string,
    lessonId: string,
    materialId: string,
    kind: MediaReviewSlotKind,
    figureKey?: string
): string {
    const base = `media-slot-${normalizeIdPart(unitId)}-${normalizeIdPart(lessonId)}-${normalizeIdPart(materialId)}`;
    if (kind === 'raster' && figureKey) {
        return `${base}-${normalizeIdPart(figureKey)}-raster`;
    }
    return `${base}-video`;
}

/** @deprecated Use mediaSlotDomId — kept for existing scroll call sites during migration. */
export function videoCardDomId(unitId: string, lessonId: string, materialId: string): string {
    return mediaSlotDomId(unitId, lessonId, materialId, 'video');
}

function supportingMaterialTypeKey(raw: unknown): string | undefined {
    if (raw == null) return undefined;
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        return trimmed === '' ? undefined : trimmed;
    }
    if (typeof raw === 'object') {
        const record = raw as Record<string, unknown>;
        const key = record.key ?? record.Key;
        if (key != null) {
            const trimmed = String(key).trim();
            return trimmed === '' ? undefined : trimmed;
        }
    }
    return undefined;
}

export function isVideoSupportingMaterial(material: SupportingMaterialDto): boolean {
    return supportingMaterialTypeKey(material.e251_MaterialType) === '30';
}

export function isVideoRemovedFromYouTube(material: SupportingMaterialDto): boolean {
    return material.e251_VideoRemovedFromYouTube === true;
}

/** True when the material has at least one YouTube clip id the teacher can select. */
export function hasSelectableVideoOptions(material: SupportingMaterialDto): boolean {
    return (material.e251_VideoOptions ?? []).some(option => Boolean(option.e251_VideoId?.trim()));
}

export function sortVideoOptions(
    options: SupportingMaterialVideoOptionDto[] | undefined
): SupportingMaterialVideoOptionDto[] {
    return [...(options ?? [])].sort(
        (a, b) => (a.e251_VideoOptionRank ?? 0) - (b.e251_VideoOptionRank ?? 0)
    );
}

export const RASTER_GENERATION_TIMEOUT_MS = 5 * 60 * 1000;

export const RASTER_GENERATION_STATUS_CHECK_OFFSETS_MS = [
    90 * 1000,
    3 * 60 * 1000,
    4 * 60 * 1000,
    5 * 60 * 1000,
] as const;

export type RasterGenerationStatusCheckTarget = {
    id: string;
    unitId: string;
    lessonId: string;
    stepId: string;
    figureKey: string;
    startedAtMs: number;
};

export type RasterGenerationStatusCheckPlan = {
    delayMs: number;
    keysToMark: string[];
    targetsToCheck: RasterGenerationStatusCheckTarget[];
};

type RasterStatusCheckUnit = {
    e251_UnitId?: string;
    e251_Lessons?: Array<{
        e251_LessonId?: string;
        e251_InstructionSteps?: Array<{
            e251_StepId?: string;
            e251_RasterFigures?: StepRasterFigureDto[];
        }>;
    }>;
};

function rasterStatusCheckTargetId(
    unitId: string,
    lessonId: string,
    stepId: string,
    figureKey: string,
    startedAtMs: number | null
): string {
    return `${unitId}|${lessonId}|${stepId}|${figureKey}|${startedAtMs ?? 'none'}`;
}

export function rasterGenerationStatusCheckKey(targetId: string, offsetMs: number): string {
    return `${targetId}:${offsetMs}`;
}

export function collectGeneratingRasterStatusCheckTargets(
    units: RasterStatusCheckUnit[] | undefined
): RasterGenerationStatusCheckTarget[] {
    const targets: RasterGenerationStatusCheckTarget[] = [];
    for (const unit of units ?? []) {
        const unitId = unit.e251_UnitId;
        if (!unitId) continue;
        for (const lesson of unit.e251_Lessons ?? []) {
            const lessonId = lesson.e251_LessonId;
            if (!lessonId) continue;
            for (const step of lesson.e251_InstructionSteps ?? []) {
                const stepId = step.e251_StepId;
                if (!stepId) continue;
                for (const figure of step.e251_RasterFigures ?? []) {
                    if (getRasterGenerationState(figure) !== 'generating') continue;
                    const figureKey = figure.e251_FigureKey;
                    if (!figureKey) continue;
                    const startedAtMs = parseRasterGenerationStartedAtMs(figure);
                    targets.push({
                        id: rasterStatusCheckTargetId(unitId, lessonId, stepId, figureKey, startedAtMs),
                        unitId,
                        lessonId,
                        stepId,
                        figureKey,
                        startedAtMs: startedAtMs ?? 0,
                    });
                }
            }
        }
    }
    return targets;
}

export function nextRasterGenerationStatusCheck(
    targets: RasterGenerationStatusCheckTarget[],
    nowMs: number,
    firedKeys: ReadonlySet<string>
): RasterGenerationStatusCheckPlan | null {
    const dueKeys: string[] = [];
    const dueById = new Map<string, RasterGenerationStatusCheckTarget>();
    let nextDelay: number | null = null;

    for (const target of targets) {
        const elapsed = nowMs - target.startedAtMs;
        for (const offset of RASTER_GENERATION_STATUS_CHECK_OFFSETS_MS) {
            const key = rasterGenerationStatusCheckKey(target.id, offset);
            if (firedKeys.has(key)) continue;
            if (elapsed >= offset) {
                dueKeys.push(key);
                dueById.set(target.id, target);
            } else {
                const delay = offset - elapsed;
                if (nextDelay == null || delay < nextDelay) nextDelay = delay;
            }
        }
    }

    if (dueKeys.length > 0) {
        return {
            delayMs: 0,
            keysToMark: dueKeys,
            targetsToCheck: [...dueById.values()],
        };
    }
    if (nextDelay != null) {
        return { delayMs: nextDelay, keysToMark: [], targetsToCheck: [] };
    }
    return null;
}

export function getRasterGenerationState(figure: StepRasterFigureDto): string {
    const state = figure.e251_GenerationState?.trim().toLowerCase();
    if (state) return state;
    if (figure.e251_ImageUrl) return 'ready';
    return 'generating';
}

export function parseRasterGenerationStartedAtMs(figure: StepRasterFigureDto): number | null {
    const raw = figure.e251_GenerationStartedAt;
    if (raw == null || raw === '') return null;
    const ms = Date.parse(String(raw));
    return Number.isFinite(ms) ? ms : null;
}

/** True when the slot is still marked generating but the 5-minute start window has elapsed. */
export function isRasterFigureGenerationTimedOut(
    figure: StepRasterFigureDto,
    nowMs = Date.now()
): boolean {
    if (getRasterGenerationState(figure) !== 'generating') return false;
    const startedAt = parseRasterGenerationStartedAtMs(figure);
    if (startedAt == null) return true;
    return nowMs - startedAt >= RASTER_GENERATION_TIMEOUT_MS;
}

export function remainingRasterGenerationMs(
    figure: StepRasterFigureDto,
    nowMs = Date.now()
): number | null {
    if (getRasterGenerationState(figure) !== 'generating') return null;
    const startedAt = parseRasterGenerationStartedAtMs(figure);
    if (startedAt == null) return 0;
    return Math.max(0, RASTER_GENERATION_TIMEOUT_MS - (nowMs - startedAt));
}

export function isRasterFigureGenerating(
    figure: StepRasterFigureDto,
    nowMs = Date.now()
): boolean {
    return (
        getRasterGenerationState(figure) === 'generating' &&
        !isRasterFigureGenerationTimedOut(figure, nowMs)
    );
}

export function isRasterFigureAwaitingImage(figure: StepRasterFigureDto): boolean {
    return getRasterGenerationState(figure) === 'awaiting_image';
}

export function isTopicImageFigure(figure: StepRasterFigureDto): boolean {
    return (figure.e251_FigureType ?? '').trim().toLowerCase() === 'topic_image';
}

/** True when this figure key is sealed on the step's worksheet section (worked-example diagram). */
export function isWorksheetRasterFigure(
    figure: StepRasterFigureDto | undefined | null,
    instructionSteps?: InstructionStepDto[],
    stepId?: string
): boolean {
    if (!figure?.e251_FigureKey || !instructionSteps?.length || !stepId) return false;
    const step = instructionSteps.find(s => s.e251_StepId && String(s.e251_StepId) === String(stepId));
    const key = figure.e251_FigureKey.trim().toLowerCase();
    return (
        step?.e251_WorksheetSection?.e251_WorksheetFigures?.some(
            f => (f.e251_Key ?? '').trim().toLowerCase() === key
        ) ?? false
    );
}

/** Upload is for reading/topic photographs — not worksheet practice diagrams. */
export function canUploadRasterFigure(
    figure: StepRasterFigureDto | undefined | null,
    instructionSteps?: InstructionStepDto[],
    stepId?: string
): boolean {
    if (!figure) return false;
    if (isWorksheetRasterFigure(figure, instructionSteps, stepId)) return false;
    return true;
}

/** Selected raster option, or the first ranked option when none is selected. */
export function resolveSelectedRasterOption(
    figure: StepRasterFigureDto
): RasterOptionDto | undefined {
    const options = sortRasterOptions(figure.e251_RasterOptions);
    if (figure.e251_SelectedOptionId) {
        const selected = options.find(option => option.e251_OptionId === figure.e251_SelectedOptionId);
        if (selected) return selected;
    }
    return options[0];
}

/**
 * True for AI-created raster images. Teacher uploads (`uploaded`) are excluded.
 * Missing source with an option present is treated as generated (legacy / default path).
 */
export function isAiGeneratedRasterFigure(figure: StepRasterFigureDto | undefined | null): boolean {
    if (!figure) return false;
    const option = resolveSelectedRasterOption(figure);
    const source = (option?.e251_Source ?? '').trim().toLowerCase();
    if (source === 'uploaded') return false;
    if (source === 'generated') return true;
    if (option) return true;
    return Boolean(figure.e251_ImageUrl?.trim());
}

export function isRasterFigureFailed(figure: StepRasterFigureDto, nowMs = Date.now()): boolean {
    return (
        getRasterGenerationState(figure) === 'failed' ||
        isRasterFigureGenerationTimedOut(figure, nowMs)
    );
}

export function sortRasterOptions(
    options: RasterOptionDto[] | undefined
): RasterOptionDto[] {
    return [...(options ?? [])].sort(
        (a, b) => (a.e251_Rank ?? 0) - (b.e251_Rank ?? 0)
    );
}

export function isRasterFigureReady(figure: StepRasterFigureDto, nowMs = Date.now()): boolean {
    if (isRasterFigureGenerating(figure, nowMs)) return false;
    if (isRasterFigureAwaitingImage(figure)) return false;
    if ((figure.e251_RasterOptions?.length ?? 0) > 0) return true;
    return getRasterGenerationState(figure) === 'ready';
}

export function getDefaultSelectedVideoId(material: SupportingMaterialDto): string | null {
    if (material.e251_SelectedVideoId) return material.e251_SelectedVideoId;
    const sorted = sortVideoOptions(material.e251_VideoOptions);
    return sorted[0]?.e251_VideoId ?? null;
}

/** Options to show in print or delivery UI — one selected video, or all during review. */
export function getDisplayVideoOptions(
    material: SupportingMaterialDto,
    options?: { includeAllForReview?: boolean }
): SupportingMaterialVideoOptionDto[] {
    const sorted = sortVideoOptions(material.e251_VideoOptions);
    if (options?.includeAllForReview) return sorted;
    const selectedId = getDefaultSelectedVideoId(material);
    if (!selectedId) return sorted.slice(0, 1);
    const match = sorted.find(option => option.e251_VideoId === selectedId);
    return match ? [match] : sorted.slice(0, 1);
}

export function getSelectedVideoId(
    material: SupportingMaterialDto,
    unitId: string,
    lessonId: string,
    overrides: Record<string, string | null | undefined>
): string | null {
    const materialId = material.e251_MaterialId;
    if (!materialId) return getDefaultSelectedVideoId(material);
    const key = videoSelectionStateKey(unitId, lessonId, materialId);
    const override = key in overrides ? overrides[key] : undefined;
    if (override) return override;
    return getDefaultSelectedVideoId(material);
}

/** Approval is valid only for the video id that was approved (client override or server state). */
export function isVideoApprovedForCurrentSelection(
    material: SupportingMaterialDto,
    selectedVideoId: string | null,
    approvedForVideoIdByKey: Record<string, string | null | undefined>,
    approvalKey: string
): boolean {
    if (isVideoRemovedFromYouTube(material)) return true;
    const normalizedSelected = selectedVideoId ?? null;
    if (approvalKey in approvedForVideoIdByKey) {
        const approvedFor = approvedForVideoIdByKey[approvalKey];
        return (approvedFor ?? null) === normalizedSelected;
    }
    if (!material.e251_VideoApproved) return false;
    const serverSelected = material.e251_SelectedVideoId ?? null;
    return serverSelected === normalizedSelected;
}

export function buildVideoSelectionsForLesson(
    lesson: LessonDetailDto,
    unitId: string,
    lessonId: string,
    overrides: Record<string, string | null | undefined>
): Array<{ C322_MaterialId: string; C322_VideoId: string | null }> {
    const selections: Array<{ C322_MaterialId: string; C322_VideoId: string | null }> = [];
    const seen = new Set<string>();

    for (const material of resolveLessonMaterials(lesson)) {
        if (!isVideoSupportingMaterial(material) || isVideoRemovedFromYouTube(material)) continue;
        if (!hasSelectableVideoOptions(material)) continue;
        const materialId = material.e251_MaterialId;
        const stepId = resolveStepIdForMediaCommand(materialId);
        if (!stepId || seen.has(stepId)) continue;
        seen.add(stepId);
        selections.push({
            C322_MaterialId: stepId,
            C322_VideoId: getSelectedVideoId(material, unitId, lessonId, overrides),
        });
    }

    return selections;
}

export function collectRasterSlotsFromLesson(lesson: LessonDetailDto): Array<{
    stepId: string;
    figure: StepRasterFigureDto;
}> {
    const slots: Array<{ stepId: string; figure: StepRasterFigureDto }> = [];
    for (const step of lesson.e251_InstructionSteps ?? []) {
        const stepId = step.e251_StepId ? String(step.e251_StepId) : undefined;
        if (!stepId) continue;
        for (const figure of step.e251_RasterFigures ?? []) {
            if (!figure.e251_FigureKey) continue;
            slots.push({ stepId, figure });
        }
    }
    return slots;
}

export function collectMediaReviewSlotsFromLesson(lesson: LessonDetailDto): MediaReviewSlotRef[] {
    const lessonId = lesson.e251_LessonId ? String(lesson.e251_LessonId) : '';
    const slots: MediaReviewSlotRef[] = [];

    for (const { stepId, figure } of collectRasterSlotsFromLesson(lesson)) {
        slots.push({
            kind: 'raster',
            lessonId,
            materialId: stepId,
            figureKey: figure.e251_FigureKey,
        });
    }

    for (const material of resolveLessonMaterials(lesson)) {
        if (!isVideoSupportingMaterial(material) || !material.e251_MaterialId) continue;
        if (isVideoRemovedFromYouTube(material)) continue;
        if (!hasSelectableVideoOptions(material)) continue;
        slots.push({
            kind: 'video',
            lessonId,
            materialId: String(material.e251_MaterialId),
        });
    }

    return slots;
}

export function isRasterFigureApproved(
    figure: StepRasterFigureDto,
    unitId: string,
    lessonId: string,
    stepId: string,
    approvedOverrides: Record<string, boolean>
): boolean {
    const figureKey = figure.e251_FigureKey;
    if (!figureKey) return true;
    const key = rasterApprovalStateKey(unitId, lessonId, stepId, figureKey);
    if (key in approvedOverrides) return approvedOverrides[key];
    return figure.e251_RasterApproved === true;
}

export function findFirstUnapprovedMediaSlot(
    unit: UnitDto,
    options: {
        isVideoApproved: (lessonId: string, material: SupportingMaterialDto) => boolean;
        isRasterApproved: (lessonId: string, stepId: string, figure: StepRasterFigureDto) => boolean;
        treatApproved?: MediaReviewSlotRef;
    }
): MediaReviewBlock | null {
    for (const lesson of unit.e251_Lessons ?? []) {
        const lessonId = lesson.e251_LessonId ? String(lesson.e251_LessonId) : '';
        for (const slot of collectMediaReviewSlotsFromLesson(lesson)) {
            if (
                options.treatApproved &&
                options.treatApproved.kind === slot.kind &&
                options.treatApproved.lessonId === slot.lessonId &&
                options.treatApproved.materialId === slot.materialId &&
                options.treatApproved.figureKey === slot.figureKey
            ) {
                continue;
            }

            if (slot.kind === 'video') {
                const material = resolveLessonMaterials(lesson).find(
                    m => m.e251_MaterialId && String(m.e251_MaterialId) === slot.materialId
                );
                if (material && !options.isVideoApproved(lessonId, material)) {
                    return { ...slot, reason: 'video-unapproved' };
                }
                continue;
            }

            const step = lesson.e251_InstructionSteps?.find(
                s => s.e251_StepId && String(s.e251_StepId) === slot.materialId
            );
            const figure = step?.e251_RasterFigures?.find(f => f.e251_FigureKey === slot.figureKey);
            if (!figure) continue;
            if (isRasterFigureGenerating(figure)) {
                return { ...slot, reason: 'image-generating' };
            }
            if (isRasterFigureAwaitingImage(figure)) {
                return { ...slot, reason: 'image-awaiting' };
            }
            if (isRasterFigureFailed(figure) && (figure.e251_RasterOptions?.length ?? 0) === 0) {
                return { ...slot, reason: 'image-failed' };
            }
            if (!options.isRasterApproved(lessonId, slot.materialId, figure)) {
                return { ...slot, reason: 'image-unapproved' };
            }
        }
    }

    return null;
}

const UNIT_APPROVAL_BLOCKED_MESSAGES: Record<UnitApprovalBlockedReason, string> = {
    'pending-video':
        'Your video approval is still saving. Wait a moment, then try Approve again.',
    'pending-image':
        'Your image approval is still saving. Wait a moment, then try Approve again.',
    'pending-media':
        'Your video and image approvals are still saving. Wait a moment, then try Approve again.',
    'video-unapproved':
        "Approve each suggested video before approving this unit. We've scrolled you to the first one that still needs a look.",
    'image-unapproved':
        "Approve each suggested image before approving this unit. We've scrolled you to the first one that still needs a look.",
    'image-generating':
        "An image is still generating. Wait for it to finish — this can take a few minutes — then try Approve again. We've scrolled you to it.",
    'image-awaiting':
        "This lesson still needs a photograph. Upload one, then try Approve again. We've scrolled you to it.",
    'image-failed':
        "An image failed to generate. Regenerate it or upload a replacement, then try Approve again. We've scrolled you to it.",
    'diagram-failed':
        "A diagram failed to generate. Regenerate it, then try Approve again. We've scrolled you to it.",
};

export function formatUnitApprovalBlockedMessage(reason: UnitApprovalBlockedReason): string {
    return UNIT_APPROVAL_BLOCKED_MESSAGES[reason];
}

export const RASTER_GENERATING_MESSAGE =
    'Image generating — please wait (this can take up to 5 minutes)';

export const RASTER_CHECK_STATUS_HINT =
    'If this takes longer than expected, use Check status.';

export const RASTER_STILL_GENERATING_AFTER_CHECK_MESSAGE =
    'Still generating — try again in a moment.';

export const RASTER_GENERATION_TIMED_OUT_MESSAGE = 'Failed — please regenerate';

export function formatRasterReviewIntro(): string {
    return 'Review the generated image for this lesson, then approve or regenerate if needed.';
}

export function formatRasterFigureLabel(figure: StepRasterFigureDto): string {
    const key = figure.e251_FigureKey?.trim();
    if (!key) return isTopicImageFigure(figure) ? 'Topic image' : 'Image';
    return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function findPlannedFigureOnStep(
    step: InstructionStepDto | undefined,
    figureKey: string | undefined
): StepPlannedFigureDto | undefined {
    if (!step || !figureKey) return undefined;
    const key = figureKey.trim().toLowerCase();
    const match = (figures: StepPlannedFigureDto[] | undefined) =>
        figures?.find(f => f.e251_Key?.trim().toLowerCase() === key);
    return (
        match(step.e251_Reading?.e251_ReadingFigures) ??
        match(step.e251_WorksheetSection?.e251_WorksheetFigures)
    );
}

/** Prompt metadata for the raster review card (debug: Raster generation prompt). */
export type RasterGenerationPromptMeta = {
    description?: string;
    figureType?: string;
    figureRole?: string;
    imageGenerationPrompt?: string;
    /** e.g. Azure AI Foundry, DashScope Qwen, Teacher upload */
    imageProvider?: string;
    /** Model id when AI-generated (defaults used by the backend). */
    imageModel?: string;
};

/** Defaults must match FoundryImageGenerationService.DefaultModel / QwenImageGenerationService.DefaultModel. */
const FOUNDRY_IMAGE_PROVIDER = 'Azure AI Foundry';
const FOUNDRY_IMAGE_MODEL = 'gpt-image-2';
const QWEN_IMAGE_PROVIDER = 'DashScope Qwen';
const QWEN_IMAGE_MODEL = 'qwen-image-3.0-pro';

export function resolveRasterImageGenerationRoute(
    figure: StepRasterFigureDto | undefined | null
): { provider: string; model?: string } | null {
    if (!figure) return null;

    const option = resolveSelectedRasterOption(figure);
    const source = (option?.e251_Source ?? '').trim().toLowerCase();
    if (source === 'uploaded') {
        return { provider: 'Teacher upload' };
    }

    const figureType = (figure.e251_FigureType ?? '').trim().toLowerCase();
    if (figureType === 'topic_image' || isTopicImageFigure(figure)) {
        return { provider: FOUNDRY_IMAGE_PROVIDER, model: FOUNDRY_IMAGE_MODEL };
    }

    // Other rasters use Qwen (model may be overridden server-side via CustomVars.QwenImageModel).
    if (figureType || option || figure.e251_ImageGenerationPrompt?.trim()) {
        return { provider: QWEN_IMAGE_PROVIDER, model: QWEN_IMAGE_MODEL };
    }

    return null;
}

export function getRasterGenerationPromptMeta(
    figure: StepRasterFigureDto,
    instructionSteps?: InstructionStepDto[],
    stepId?: string
): RasterGenerationPromptMeta | null {
    const step = instructionSteps?.find(s => s.e251_StepId && String(s.e251_StepId) === String(stepId));
    const planned = findPlannedFigureOnStep(step, figure.e251_FigureKey);

    const description = figure.e251_FigurePurpose?.trim() || planned?.e251_FigurePurpose?.trim();
    const figureType = figure.e251_FigureType?.trim() || planned?.e251_FigureType?.trim();
    const figureRole = figure.e251_Role?.trim() || planned?.e251_Role?.trim();
    const imageGenerationPrompt = figure.e251_ImageGenerationPrompt?.trim();
    const route = resolveRasterImageGenerationRoute({
        ...figure,
        e251_FigureType: figure.e251_FigureType || planned?.e251_FigureType,
    });

    if (!description && !figureType && !figureRole && !imageGenerationPrompt && !route) {
        return null;
    }
    return {
        description,
        figureType,
        figureRole,
        imageGenerationPrompt,
        imageProvider: route?.provider,
        imageModel: route?.model,
    };
}

export function lessonHasMediaReviewSlots(lesson: LessonDetailDto): boolean {
    return collectMediaReviewSlotsFromLesson(lesson).length > 0;
}

export function instructionStepHasRasterFigures(step: InstructionStepDto): boolean {
    return (step.e251_RasterFigures?.length ?? 0) > 0;
}
